import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import CertificateList from "../../../components/Certificate/CertificateList";
import ExpandableSection from "../../../components/ExpandableSection";
import CourseStatusIcon from "../../../components/CourseStatusIcon";
import { formatDate } from "../../../services/formatters";

interface OperationDetailViewProps {
  operation: {
    id: number;
    name: string;
    status: string | null;
    validUntil: string | null;
    courseIds?: string[];
  };
  position: {
    id: number;
    name: string;
    status: string | null;
    validUntil: string | null;
    courseIds?: string[];
  };
  certificateMap: Record<string, any>;
  showPositionCoursesCollapsed?: boolean;
  defaultPositionCoursesExpanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
}

const OperationDetailView: FC<OperationDetailViewProps> = ({
  operation,
  position,
  certificateMap,
  showPositionCoursesCollapsed = true,
  defaultPositionCoursesExpanded = false,
  onExpandChange,
}) => {
  const { t } = useTranslation();
  const [positionCoursesExpanded, setPositionCoursesExpanded] = useState(defaultPositionCoursesExpanded);

  const handleExpandChange = (expanded: boolean) => {
    setPositionCoursesExpanded(expanded);
    if (onExpandChange) {
      onExpandChange(expanded);
    }
  };

  const positionCourseIds = position?.courseIds || [];
  const operationCourseIds = operation?.courseIds || [];
  const positionCertificates = positionCourseIds.map((courseId) => certificateMap[courseId]).filter(Boolean);
  const operationCertificates = operationCourseIds.map((courseId) => certificateMap[courseId]).filter(Boolean);

  return (
    <>
      {showPositionCoursesCollapsed && positionCertificates.length > 0 && (
        <Box
          sx={{
            mb: 2,
          }}
        >
          <ExpandableSection
            title={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <CourseStatusIcon status={position?.status} />
                  <Typography variant="body2" style={{ fontWeight: 500, marginLeft: ".5rem" }}>
                    {position?.name} {t("courses")} ({positionCertificates.length})
                  </Typography>
                </Box>
                {position?.validUntil && (
                  <Typography variant="body2" style={{ color: "#666", fontSize: "0.85em", marginLeft: "1rem" }}>
                    {formatDate(position.validUntil)}
                  </Typography>
                )}
              </Box>
            }
            navId={`position-courses-${position?.id}`}
            expanded={positionCoursesExpanded}
            onChange={handleExpandChange}
          >
            <Box
              sx={{
                ml: 2,
              }}
            >
              <CertificateList certificates={positionCertificates} variant="default" />
            </Box>
          </ExpandableSection>
        </Box>
      )}
      {operationCertificates.length > 0 ? (
        <CertificateList certificates={operationCertificates} variant="default" />
      ) : (
        !showPositionCoursesCollapsed && (
          <Typography variant="body2" color="textSecondary">
            {t("compliance.no_operation_courses")}
          </Typography>
        )
      )}
    </>
  );
};

export default OperationDetailView;
